import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { formatTimestamp, formatDate } from '../../utils/dateTime';
import {
  Gavel,
  Plus,
  FileText,
  Clock,
  CheckCircle,
  AlertCircle,
  ChevronRight,
  Send,
  Edit,
  Eye,
  Download,
  Copy,
  Sparkles,
  Target,
  Loader2,
  X,
  Mail,
  Truck,
  MapPin,
  Wand2,
} from 'lucide-react';
import { DisputeWizard } from '../../components/disputes/DisputeWizard';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const statusConfig = {
  draft: { label: 'Draft', color: 'bg-stone-100 text-stone-700 dark:bg-stone-800/40 dark:text-stone-300', icon: Edit },
  sent: { label: 'Sent', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300', icon: Send },
  mailed: { label: 'Mailed', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300', icon: Mail },
  monitoring: { label: 'Monitoring', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300', icon: Clock },
  completed: { label: 'Completed', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300', icon: CheckCircle },
  clarification: { label: 'Needs Clarification', color: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300', icon: AlertCircle }
};

// Dispute Card Component
const DisputeCard = ({ dispute, onGenerateLetter, onViewLetter, onMailLetter }) => {
  const status = statusConfig[dispute.status] || statusConfig.draft;
  const StatusIcon = status.icon;
  const hasLetter = dispute.letters && dispute.letters.length > 0;
  const letterMailed = hasLetter && dispute.letters[0]?.status === 'mailed';

  return (
    <div className="elite-card p-5 hover:shadow-md transition-shadow" data-testid={`dispute-${dispute.dispute_id}`}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-stone-100 dark:bg-emerald-900/30 flex items-center justify-center">
            <Gavel size={20} className="text-stone-600 dark:text-emerald-400" />
          </div>
          <div>
            <p className="font-semibold text-stone-900 dark:text-emerald-50">{dispute.creditor_name}</p>
            <p className="text-sm text-stone-500 dark:text-emerald-300/70">Round {dispute.round_number} • {dispute.target_bureau}</p>
          </div>
        </div>
        <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${status.color}`}>
          <StatusIcon size={12} />
          {status.label}
        </span>
      </div>

      <div className="mb-4">
        <p className="text-sm text-stone-600 dark:text-emerald-200/80 line-clamp-2">{dispute.dispute_reason}</p>
      </div>

      <div className="flex items-center justify-between">
        <div className="text-xs text-stone-500 dark:text-emerald-300/60">
          Created: {formatDate(dispute.created_at)}
        </div>
        <div className="flex items-center gap-2">
          {hasLetter ? (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onViewLetter(dispute)}
                data-testid={`view-letter-${dispute.dispute_id}`}
              >
                <Eye size={16} className="mr-1" />
                View
              </Button>
              {!letterMailed && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                  onClick={() => onMailLetter(dispute)}
                  data-testid={`mail-letter-${dispute.dispute_id}`}
                >
                  <Mail size={16} className="mr-1" />
                  Mail Letter
                </Button>
              )}
              {letterMailed && (
                <span className="text-xs px-2 py-1 bg-emerald-50 text-emerald-700 rounded-full flex items-center gap-1">
                  <Truck size={12} />
                  Mailed
                </span>
              )}
            </>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onGenerateLetter(dispute)}
              data-testid={`generate-letter-${dispute.dispute_id}`}
            >
              <Sparkles size={16} className="mr-1" />
              Generate Letter
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// Create Dispute Modal
const CreateDisputeModal = ({ open, onClose, tradeline, onSubmit }) => {
  const [reason, setReason] = useState('');
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);

  const disputeReasons = [
    'I do not recognize this account',
    'This account was paid in full',
    'The balance is incorrect',
    'The payment history is inaccurate',
    'This account is not mine (identity theft)',
    'This account was included in bankruptcy',
    'The dates are incorrect',
    'This account was settled',
    'Other'
  ];

  const handleSubmit = async () => {
    if (!reason) {
      toast.error('Please select a dispute reason');
      return;
    }

    setLoading(true);
    try {
      await onSubmit({
        tradeline_id: tradeline.tradeline_id,
        dispute_reason: reason,
        user_answers: answers
      });
      onClose();
    } catch (error) {
      toast.error('Failed to create dispute');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Create New Dispute</DialogTitle>
          <DialogDescription>
            Dispute for: {tradeline?.creditor_name}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          <div>
            <label className="text-sm font-medium text-stone-700">Dispute Reason</label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger className="mt-1.5" data-testid="dispute-reason-select">
                <SelectValue placeholder="Select a reason..." />
              </SelectTrigger>
              <SelectContent>
                {disputeReasons.map((r) => (
                  <SelectItem key={r} value={r}>{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium text-stone-700">Additional Explanation</label>
            <Textarea
              placeholder="Provide any additional details about your dispute..."
              className="mt-1.5"
              value={answers.explanation || ''}
              onChange={(e) => setAnswers({ ...answers, explanation: e.target.value })}
              data-testid="dispute-explanation"
            />
            <p className="text-xs text-stone-500 mt-1">
              Example: I never opened or authorized this account. The creditor has not provided verification.
            </p>
          </div>

          <div className="p-3 bg-stone-50 rounded-lg">
            <div className="flex items-center gap-2 text-sm text-stone-600">
              <Target size={16} className="text-emerald-500" />
              <span>AI will recommend bureau vs furnisher strategy</span>
            </div>
          </div>
        </div>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            disabled={loading}
            className="btn-primary"
            data-testid="submit-dispute"
          >
            {loading ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
            Create Dispute
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Letter Viewer Modal
const LetterViewerModal = ({ open, onClose, dispute }) => {
  const [generating, setGenerating] = useState(false);
  const { getAuthHeaders } = useAuth();
  const letter = dispute?.letters?.[0];

  const handleCopy = () => {
    navigator.clipboard.writeText(letter?.content || '');
    toast.success('Letter copied to clipboard');
  };

  const handleDownload = () => {
    const blob = new Blob([letter?.content || ''], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dispute_letter_${dispute?.dispute_id}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!letter) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText size={20} />
            Dispute Letter - {dispute?.creditor_name}
          </DialogTitle>
          <DialogDescription>
            {letter.letter_type === 'bureau' ? 'Bureau' : 'Furnisher'} Letter • 
            {letter.target_bureau && ` ${letter.target_bureau}`} • 
            Status: {letter.status}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto mt-4">
          <div className="bg-stone-50 rounded-lg p-6 font-mono text-sm whitespace-pre-wrap">
            {letter.content}
          </div>
        </div>

        <DialogFooter className="mt-4 flex-shrink-0">
          <div className="flex items-center gap-2 w-full justify-between">
            <div className="flex items-center gap-2">
              <span className="text-xs text-stone-500 flex items-center gap-1">
                <Sparkles size={12} className="text-amber-500" />
                AI Generated Draft
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleCopy}>
                <Copy size={14} className="mr-1" />
                Copy
              </Button>
              <Button variant="outline" size="sm" onClick={handleDownload}>
                <Download size={14} className="mr-1" />
                Download
              </Button>
              <Button variant="outline" size="sm">
                <Edit size={14} className="mr-1" />
                Edit
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Mail Letter Modal
const MailLetterModal = ({ open, onClose, dispute, onSuccess }) => {
  const { getAuthHeaders } = useAuth();
  const [mailType, setMailType] = useState('first_class');
  const [senderAddress, setSenderAddress] = useState({
    name: '',
    line1: '',
    line2: '',
    city: '',
    state: '',
    postal_code: ''
  });
  const [loading, setLoading] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);

  const letter = dispute?.letters?.[0];
  const bureau = letter?.target_bureau;

  // Load user profile for sender address
  useEffect(() => {
    if (open) {
      loadUserProfile();
    }
  }, [open]);

  const loadUserProfile = async () => {
    try {
      const response = await axios.get(`${API}/users/profile`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      const profile = response.data;
      setSenderAddress({
        name: profile.name || '',
        line1: profile.mailing_address?.line1 || '',
        line2: profile.mailing_address?.line2 || '',
        city: profile.mailing_address?.city || '',
        state: profile.mailing_address?.state || profile.state || '',
        postal_code: profile.mailing_address?.postal_code || ''
      });
    } catch (error) {
      console.error('Failed to load profile:', error);
    } finally {
      setLoadingProfile(false);
    }
  };

  const handleSaveAndMail = async () => {
    // Validate sender address
    if (!senderAddress.name || !senderAddress.line1 || !senderAddress.city || 
        !senderAddress.state || !senderAddress.postal_code) {
      toast.error('Please complete your return address');
      return;
    }

    setLoading(true);
    try {
      // Save sender address to profile
      await axios.put(`${API}/users/profile`, {
        name: senderAddress.name,
        address_line1: senderAddress.line1,
        address_line2: senderAddress.line2,
        city: senderAddress.city,
        state: senderAddress.state,
        postal_code: senderAddress.postal_code
      }, {
        headers: getAuthHeaders(),
        withCredentials: true
      });

      // Send letter via PostGrid
      const response = await axios.post(
        `${API}/mailing/send-to-bureau?dispute_id=${dispute.dispute_id}&letter_id=${letter.letter_id}&mail_type=${mailType}`,
        {},
        { headers: getAuthHeaders(), withCredentials: true }
      );

      toast.success(`Letter sent to ${bureau} via ${mailType === 'certified' ? 'Certified Mail' : 'First-Class Mail'}!`);
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Mailing error:', error);
      toast.error(error.response?.data?.detail || 'Failed to send letter');
    } finally {
      setLoading(false);
    }
  };

  if (!letter) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail size={20} className="text-emerald-600" />
            Mail Dispute Letter
          </DialogTitle>
          <DialogDescription>
            Send to {bureau} for {dispute?.creditor_name}
          </DialogDescription>
        </DialogHeader>

        {loadingProfile ? (
          <div className="py-8 text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-emerald-500" />
            <p className="text-sm text-stone-500 mt-2">Loading your information...</p>
          </div>
        ) : (
          <div className="space-y-4 mt-4">
            {/* Mail Type Selection */}
            <div>
              <Label className="text-stone-700 font-medium">Mail Type</Label>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <button
                  onClick={() => setMailType('first_class')}
                  className={`p-3 rounded-xl border text-left transition-all ${
                    mailType === 'first_class' 
                      ? 'border-emerald-500 bg-emerald-50' 
                      : 'border-stone-200 hover:border-stone-300'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Mail size={16} className={mailType === 'first_class' ? 'text-emerald-600' : 'text-stone-500'} />
                    <span className="font-medium text-sm">First-Class</span>
                  </div>
                  <p className="text-xs text-stone-500">Standard mail delivery</p>
                </button>
                <button
                  onClick={() => setMailType('certified')}
                  className={`p-3 rounded-xl border text-left transition-all ${
                    mailType === 'certified' 
                      ? 'border-emerald-500 bg-emerald-50' 
                      : 'border-stone-200 hover:border-stone-300'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Truck size={16} className={mailType === 'certified' ? 'text-emerald-600' : 'text-stone-500'} />
                    <span className="font-medium text-sm">Certified Mail</span>
                  </div>
                  <p className="text-xs text-stone-500">Tracking + Signature</p>
                </button>
              </div>
            </div>

            {/* Sender Address */}
            <div>
              <Label className="text-stone-700 font-medium flex items-center gap-2">
                <MapPin size={14} />
                Your Return Address
              </Label>
              <div className="grid grid-cols-1 gap-3 mt-2">
                <Input
                  placeholder="Full Name"
                  value={senderAddress.name}
                  onChange={(e) => setSenderAddress({...senderAddress, name: e.target.value})}
                />
                <Input
                  placeholder="Street Address"
                  value={senderAddress.line1}
                  onChange={(e) => setSenderAddress({...senderAddress, line1: e.target.value})}
                />
                <Input
                  placeholder="Apt, Suite, etc. (optional)"
                  value={senderAddress.line2}
                  onChange={(e) => setSenderAddress({...senderAddress, line2: e.target.value})}
                />
                <div className="grid grid-cols-3 gap-2">
                  <Input
                    placeholder="City"
                    value={senderAddress.city}
                    onChange={(e) => setSenderAddress({...senderAddress, city: e.target.value})}
                  />
                  <Input
                    placeholder="State"
                    value={senderAddress.state}
                    onChange={(e) => setSenderAddress({...senderAddress, state: e.target.value})}
                    maxLength={2}
                    className="uppercase"
                  />
                  <Input
                    placeholder="ZIP"
                    value={senderAddress.postal_code}
                    onChange={(e) => setSenderAddress({...senderAddress, postal_code: e.target.value})}
                  />
                </div>
              </div>
            </div>

            {/* Recipient Info */}
            <div className="p-3 bg-stone-50 rounded-lg">
              <p className="text-xs font-medium text-stone-500 uppercase tracking-wide mb-1">Sending To</p>
              <p className="text-sm font-medium text-stone-900">{bureau}</p>
              <p className="text-xs text-stone-500">
                Bureau dispute address will be used automatically
              </p>
            </div>

            {/* Cost Estimate */}
            <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-100">
              <div className="flex items-center justify-between">
                <span className="text-sm text-emerald-800">Estimated Cost</span>
                <span className="font-semibold text-emerald-700">
                  {mailType === 'certified' ? '$4.50 - $6.00' : '$1.00 - $1.50'}
                </span>
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button 
            onClick={handleSaveAndMail} 
            disabled={loading || loadingProfile}
            className="btn-primary"
            data-testid="confirm-mail-letter"
          >
            {loading ? <Loader2 size={16} className="animate-spin mr-2" /> : <Send size={16} className="mr-2" />}
            Send Letter
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Main Dispute Center Component
const DisputeCenter = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { getAuthHeaders, token } = useAuth();
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [showLetterModal, setShowLetterModal] = useState(false);
  const [showMailModal, setShowMailModal] = useState(false);
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [selectedTradeline, setSelectedTradeline] = useState(null);

  useEffect(() => {
    fetchDisputes();
    
    // Check if navigated with tradeline for new dispute
    if (location.state?.tradeline) {
      setSelectedTradeline(location.state.tradeline);
      // Use the new wizard for disputes
      setShowWizard(true);
    }
  }, [location.state]);

  const fetchDisputes = async () => {
    try {
      const response = await axios.get(`${API}/disputes`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setDisputes(response.data.disputes || []);
    } catch (error) {
      console.error('Error fetching disputes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDispute = async (disputeData) => {
    try {
      // Format data for backend - support both old and new wizard formats
      const payload = {
        tradeline_id: disputeData.tradeline_id,
        dispute_reason: disputeData.dispute_reason || disputeData.selected_reasons?.join(', ') || '',
        selected_reasons: disputeData.selected_reasons || [],
        routing_strategy: disputeData.routing_strategy || null,
        template_family: disputeData.template_family || null,
        prompt_keys: disputeData.prompt_keys || [],
        dispute_round: disputeData.dispute_round || 1,
        user_answers: disputeData.user_answers || {},
      };
      
      const response = await axios.post(`${API}/disputes`, payload, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      
      toast.success('Dispute created successfully with AI-optimized strategy');
      fetchDisputes();
      
      // Return the created dispute for the wizard to use
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create dispute');
      throw error;
    }
  };

  const handleGenerateLetter = async (dispute) => {
    try {
      toast.loading('AI is generating your dispute letter...');
      
      await axios.post(`${API}/disputes/${dispute.dispute_id}/generate-letter`, 
        { dispute_id: dispute.dispute_id, letter_type: 'bureau' },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      
      toast.dismiss();
      toast.success('Letter generated successfully');
      fetchDisputes();
    } catch (error) {
      toast.dismiss();
      toast.error('Failed to generate letter');
    }
  };

  const handleViewLetter = (dispute) => {
    setSelectedDispute(dispute);
    setShowLetterModal(true);
  };

  const handleMailLetter = (dispute) => {
    setSelectedDispute(dispute);
    setShowMailModal(true);
  };

  // Filter disputes
  const filteredDisputes = disputes.filter(d => {
    if (activeTab === 'all') return true;
    return d.status === activeTab;
  });

  // Stats
  const stats = {
    total: disputes.length,
    draft: disputes.filter(d => d.status === 'draft').length,
    sent: disputes.filter(d => d.status === 'sent').length,
    monitoring: disputes.filter(d => d.status === 'monitoring').length,
    completed: disputes.filter(d => d.status === 'completed').length
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-12" />
        <div className="grid grid-cols-2 gap-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-40" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dispute-center">
      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="elite-card p-4">
          <p className="text-2xl font-bold text-stone-900">{stats.total}</p>
          <p className="text-sm text-stone-500">Total Disputes</p>
        </div>
        <div className="elite-card p-4">
          <p className="text-2xl font-bold text-stone-600">{stats.draft}</p>
          <p className="text-sm text-stone-500">Drafts</p>
        </div>
        <div className="elite-card p-4">
          <p className="text-2xl font-bold text-emerald-600">{stats.sent}</p>
          <p className="text-sm text-stone-500">Sent</p>
        </div>
        <div className="elite-card p-4">
          <p className="text-2xl font-bold text-amber-600">{stats.monitoring}</p>
          <p className="text-sm text-stone-500">Monitoring</p>
        </div>
        <div className="elite-card p-4">
          <p className="text-2xl font-bold text-emerald-600">{stats.completed}</p>
          <p className="text-sm text-stone-500">Completed</p>
        </div>
      </div>

      {/* Actions Bar */}
      <div className="flex items-center justify-between">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="draft">Drafts</TabsTrigger>
            <TabsTrigger value="sent">Sent</TabsTrigger>
            <TabsTrigger value="monitoring">Monitoring</TabsTrigger>
            <TabsTrigger value="completed">Completed</TabsTrigger>
          </TabsList>
        </Tabs>

        <Button
          onClick={() => {
            setSelectedTradeline(null);
            navigate('/credit-report/negative');
          }}
          className="btn-primary"
          data-testid="new-dispute-btn"
        >
          <Plus size={18} className="mr-2" />
          New Dispute
        </Button>
      </div>

      {/* Disputes Grid */}
      {filteredDisputes.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredDisputes.map(dispute => (
            <DisputeCard
              key={dispute.dispute_id}
              dispute={dispute}
              onGenerateLetter={handleGenerateLetter}
              onViewLetter={handleViewLetter}
              onMailLetter={handleMailLetter}
            />
          ))}
        </div>
      ) : (
        <div className="elite-card p-12 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-stone-100 flex items-center justify-center">
            <Gavel size={28} className="text-stone-400" />
          </div>
          <h3 className="text-lg font-semibold text-stone-900 mb-2">No Disputes Yet</h3>
          <p className="text-stone-500 mb-4">
            Start by reviewing your negative accounts and creating disputes.
          </p>
          <Button
            onClick={() => navigate('/credit-report/negative')}
            className="btn-primary"
          >
            Review Negative Accounts
          </Button>
        </div>
      )}

      {/* Modals */}
      {/* Legacy Create Modal - keeping for backward compatibility */}
      <CreateDisputeModal
        open={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setSelectedTradeline(null);
        }}
        tradeline={selectedTradeline}
        onSubmit={handleCreateDispute}
      />

      {/* NEW: Dispute Wizard with Architecture v1 Integration */}
      <DisputeWizard
        open={showWizard}
        onClose={() => {
          setShowWizard(false);
          setSelectedTradeline(null);
          // Clear navigation state
          navigate(location.pathname, { replace: true });
        }}
        account={selectedTradeline}
        onSubmit={handleCreateDispute}
        token={token}
      />

      <LetterViewerModal
        open={showLetterModal}
        onClose={() => setShowLetterModal(false)}
        dispute={selectedDispute}
      />

      <MailLetterModal
        open={showMailModal}
        onClose={() => setShowMailModal(false)}
        dispute={selectedDispute}
        onSuccess={fetchDisputes}
      />
    </div>
  );
};

export default DisputeCenter;
